import { useMemo, useState, type JSX, type ReactNode } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import {
  AlertCircle,
  ArrowLeft,
  Ban,
  CircleCheck,
  CircleDashed,
  Clock,
  Link2,
  Send,
} from 'lucide-react';
import {
  Badge,
  type BadgeProps,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Input,
  Label,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from '@caratuva/ui';
import { canTransition, type InvoiceStatus } from '@caratuva/shared-types';
import { ApiError, cancelInvoice, getInvoice, inviteBuyer } from '../lib/api';

/*
 * GetPaid invoice detail. Simplified consumer view: header + line items +
 * payment timeline, plus a prominent payment-link card (copy / email to
 * buyer) and a cancel action gated on the shared invoice state machine.
 */

export const Route = createFileRoute('/_app/invoices/$id')({
  component: InvoiceDetail,
});

// Statuses where the buyer has already been invited but hasn't paid yet.
// /invite-buyer is idempotent in these states — it just redelivers the
// magic-link email — so we keep the action enabled and relabel it "Resend".
const RESEND_INVITE_STATES: ReadonlySet<InvoiceStatus> = new Set([
  'awaiting_buyer',
  'buyer_kyc_pending',
  'buyer_kyc_approved',
  'fx_quoted',
  'fx_expired',
]);

const DEFAULT_BUYER_PORTAL_URL = 'http://localhost:5174';

function buyerPortalUrl(): string {
  const fromEnv = import.meta.env.VITE_BUYER_PORTAL_URL;
  if (typeof fromEnv === 'string' && fromEnv.length > 0) return fromEnv;
  return DEFAULT_BUYER_PORTAL_URL;
}

const STATUS_VARIANT: Record<InvoiceStatus, BadgeProps['variant']> = {
  draft: 'secondary',
  awaiting_approval: 'warning',
  approved: 'info',
  awaiting_buyer: 'info',
  buyer_kyc_pending: 'warning',
  buyer_kyc_approved: 'info',
  buyer_kyc_rejected: 'danger',
  fx_quoted: 'info',
  fx_expired: 'warning',
  on_ramp_pending: 'warning',
  on_ramp_failed: 'danger',
  fiat_received: 'info',
  on_chain_confirmed: 'info',
  offramp_pending: 'warning',
  offramp_failed: 'danger',
  settled: 'success',
  partial_payment: 'warning',
  expired: 'secondary',
  cancelled: 'destructive',
  restored: 'info',
  manual_review: 'warning',
};

function InvoiceDetail(): JSX.Element {
  const intl = useIntl();
  const { id } = Route.useParams();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['invoice', id],
    queryFn: ({ signal }) => getInvoice(id, signal),
  });

  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelError, setCancelError] = useState<string | null>(null);

  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);

  function extractApiMessage(err: unknown, fallback: string): string {
    if (err instanceof ApiError) {
      const body = err.body as { message?: unknown } | null;
      return Array.isArray(body?.message)
        ? (body.message as string[]).join('; ')
        : typeof body?.message === 'string'
          ? body.message
          : err.message;
    }
    return err instanceof Error ? err.message : fallback;
  }

  const cancelMutation = useMutation({
    mutationFn: (reason: string) => cancelInvoice(id, reason),
    onSuccess: (data) => {
      queryClient.setQueryData(['invoice', id], data);
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
      setCancelDialogOpen(false);
      setCancelReason('');
      setCancelError(null);
    },
    onError: (err) => {
      setCancelError(
        extractApiMessage(
          err,
          intl.formatMessage({
            id: 'invoices.detail.cancel.error',
            defaultMessage: 'Failed to cancel invoice.',
          }),
        ),
      );
    },
  });

  const inviteMutation = useMutation({
    mutationFn: () => inviteBuyer(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['invoice', id] });
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
      setInviteDialogOpen(false);
      setInviteError(null);
    },
    onError: (err) => {
      setInviteError(
        extractApiMessage(
          err,
          intl.formatMessage({
            id: 'invoices.detail.action.invite.error',
            defaultMessage: 'Failed to invite buyer.',
          }),
        ),
      );
    },
  });

  const invoice = query.data;
  const cancelEnabled = useMemo(
    () => (invoice ? canTransition(invoice.status as InvoiceStatus, 'cancelled') : false),
    [invoice],
  );
  const isResend = useMemo(
    () => (invoice ? RESEND_INVITE_STATES.has(invoice.status as InvoiceStatus) : false),
    [invoice],
  );
  const inviteEnabled = useMemo(() => {
    if (!invoice) return false;
    if (isResend) return true;
    return canTransition(invoice.status as InvoiceStatus, 'buyer_invited');
  }, [invoice, isResend]);
  const paymentLinkEnabled = inviteEnabled;

  const paymentLink = invoice ? `${buyerPortalUrl()}/r/${invoice.publicId}` : null;
  async function copyPaymentLink(): Promise<void> {
    if (!paymentLink) return;
    try {
      await navigator.clipboard.writeText(paymentLink);
      toast(intl.formatMessage({ id: 'common.copied', defaultMessage: 'Copied' }));
    } catch {
      toast(
        intl.formatMessage({
          id: 'invoices.detail.paymentLink.copyError',
          defaultMessage: 'Could not copy link',
        }),
      );
    }
  }

  return (
    <section className="mx-auto max-w-4xl space-y-6">
      <Link
        to="/invoices"
        className="inline-flex items-center gap-1 text-sm text-navy-700 transition-colors hover:text-navy-900"
      >
        <ArrowLeft aria-hidden className="h-4 w-4" strokeWidth={1.5} />
        {intl.formatMessage({ id: 'common.nav.invoices', defaultMessage: 'Invoices' })}
      </Link>

      {query.isPending ? (
        <p className="text-sm text-navy-600">
          {intl.formatMessage({
            id: 'invoices.detail.loading',
            defaultMessage: 'Loading invoice...',
          })}
        </p>
      ) : query.isError || !invoice ? (
        <p className="text-sm text-[var(--color-danger)]">
          {intl.formatMessage({
            id: 'invoices.detail.error',
            defaultMessage: 'Failed to load invoice.',
          })}
        </p>
      ) : (
        <>
          <div className="border-b border-navy-100 pb-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="space-y-1">
                <p className="font-mono text-xs text-navy-600">{invoice.publicId}</p>
                <h1 className="text-2xl font-semibold tracking-tight text-navy-900">
                  {intl.formatMessage({ id: 'invoices.detail.title', defaultMessage: 'Invoice' })}
                </h1>
              </div>
              <Badge
                variant={STATUS_VARIANT[invoice.status as InvoiceStatus] ?? 'secondary'}
                className="font-mono text-[11px] uppercase tracking-wider"
              >
                {invoice.status}
              </Badge>
            </div>
            <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              <DetailField
                label={intl.formatMessage({
                  id: 'invoices.detail.field.buyer',
                  defaultMessage: 'Buyer',
                })}
              >
                {invoice.buyerName ? (
                  <span className="block truncate" title={invoice.buyerName}>
                    {invoice.buyerName}
                  </span>
                ) : invoice.buyerEmail ? (
                  <span className="block truncate" title={invoice.buyerEmail}>
                    {invoice.buyerEmail}
                  </span>
                ) : (
                  <span className="block truncate font-mono text-xs" title={invoice.buyerId}>
                    {invoice.buyerId}
                  </span>
                )}
              </DetailField>
              {invoice.buyerEmail && invoice.buyerName ? (
                <DetailField
                  label={intl.formatMessage({
                    id: 'invoices.detail.field.buyerEmail',
                    defaultMessage: 'Buyer email',
                  })}
                >
                  <span className="block truncate" title={invoice.buyerEmail}>
                    {invoice.buyerEmail}
                  </span>
                </DetailField>
              ) : null}
              <DetailField
                label={intl.formatMessage({
                  id: 'invoices.detail.field.total',
                  defaultMessage: 'Total',
                })}
              >
                <span className="font-mono tabular-nums font-medium">{invoice.amount}</span>
                <span className="ml-1 text-navy-700">{invoice.currency}</span>
              </DetailField>
              <DetailField
                label={intl.formatMessage({
                  id: 'invoices.detail.field.createdAt',
                  defaultMessage: 'Created',
                })}
              >
                {formatDateTime(invoice.createdAt, intl.locale)}
              </DetailField>
              <DetailField
                label={intl.formatMessage({
                  id: 'invoices.detail.field.dueDate',
                  defaultMessage: 'Due date',
                })}
              >
                {invoice.dueDate ? formatDate(invoice.dueDate, intl.locale) : '—'}
              </DetailField>
              {invoice.cancelReason ? (
                <DetailField
                  label={intl.formatMessage({
                    id: 'invoices.detail.field.cancelReason',
                    defaultMessage: 'Cancel reason',
                  })}
                >
                  {invoice.cancelReason}
                </DetailField>
              ) : null}
            </div>
          </div>

          <div className="grid gap-8 lg:grid-cols-[1fr_300px]">
            <div className="space-y-8">
              <section>
                <header className="mb-4 space-y-1">
                  <h2 className="text-base font-semibold text-navy-900">
                    {intl.formatMessage({
                      id: 'invoices.detail.section.lineItems',
                      defaultMessage: 'Line items',
                    })}
                  </h2>
                </header>
                <Card className="overflow-hidden border-navy-100 shadow-xs">
                  <CardContent className="p-0">
                    {invoice.lineItems && invoice.lineItems.length > 0 ? (
                      <Table>
                        <TableHeader>
                          <TableRow className="border-navy-100 hover:bg-transparent">
                            <TableHead className="h-11 pl-6 text-[11px] font-medium uppercase tracking-wider text-navy-700">
                              {intl.formatMessage({
                                id: 'invoices.detail.column.description',
                                defaultMessage: 'Description',
                              })}
                            </TableHead>
                            <TableHead className="h-11 text-right text-[11px] font-medium uppercase tracking-wider text-navy-700">
                              {intl.formatMessage({
                                id: 'invoices.detail.column.quantity',
                                defaultMessage: 'Qty',
                              })}
                            </TableHead>
                            <TableHead className="h-11 text-right text-[11px] font-medium uppercase tracking-wider text-navy-700">
                              {intl.formatMessage({
                                id: 'invoices.detail.column.unitPrice',
                                defaultMessage: 'Unit price',
                              })}
                            </TableHead>
                            <TableHead className="h-11 pr-6 text-right text-[11px] font-medium uppercase tracking-wider text-navy-700">
                              {intl.formatMessage({
                                id: 'invoices.detail.column.subtotal',
                                defaultMessage: 'Subtotal',
                              })}
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {invoice.lineItems.map((li) => (
                            <TableRow key={li.id} className="border-navy-100 hover:bg-beige-50/40">
                              <TableCell className="py-3 pl-6 text-navy-900">
                                {li.description}
                              </TableCell>
                              <TableCell className="py-3 text-right tabular-nums text-navy-900">
                                {li.quantity}
                              </TableCell>
                              <TableCell className="py-3 text-right tabular-nums text-navy-900">
                                {li.unitPrice}
                              </TableCell>
                              <TableCell className="py-3 pr-6 text-right font-medium tabular-nums text-navy-900">
                                {li.subtotal}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <p className="px-6 py-4 text-sm text-navy-600">
                        {intl.formatMessage({
                          id: 'invoices.detail.lineItems.empty',
                          defaultMessage: 'No line items.',
                        })}
                      </p>
                    )}
                  </CardContent>
                </Card>
              </section>

              <section>
                <header className="mb-4 space-y-1">
                  <h2 className="text-base font-semibold text-navy-900">
                    {intl.formatMessage({
                      id: 'invoices.detail.section.timeline',
                      defaultMessage: 'Payment timeline',
                    })}
                  </h2>
                </header>
                <Card className="border-navy-100 shadow-xs">
                  <CardContent className="p-6">
                    <Timeline events={invoice.timeline} />
                  </CardContent>
                </Card>
              </section>
            </div>

            <aside className="space-y-4 lg:sticky lg:top-6 lg:self-start">
              <section>
                <header className="mb-4 space-y-1">
                  <h2 className="text-base font-semibold text-navy-900">
                    {intl.formatMessage({
                      id: 'invoices.detail.paymentLink.title',
                      defaultMessage: 'Payment link',
                    })}
                  </h2>
                </header>
                <Card className="border-navy-100 shadow-xs">
                  <CardContent className="space-y-3 p-4">
                    {paymentLinkEnabled && paymentLink ? (
                      <p className="break-all rounded-md border border-navy-100 bg-beige-50/60 p-2 font-mono text-xs text-navy-800">
                        {paymentLink}
                      </p>
                    ) : (
                      <p className="text-xs text-navy-600">
                        {intl.formatMessage({
                          id: 'invoices.detail.paymentLink.disabledHint',
                          defaultMessage:
                            'The payment link is no longer relevant for this invoice.',
                        })}
                      </p>
                    )}
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full"
                      disabled={!paymentLinkEnabled}
                      onClick={copyPaymentLink}
                    >
                      <Link2 aria-hidden className="h-4 w-4" strokeWidth={1.5} />
                      {intl.formatMessage({
                        id: 'invoices.detail.paymentLink.copyButton',
                        defaultMessage: 'Copy payment link',
                      })}
                    </Button>

                    <Dialog
                      open={inviteDialogOpen}
                      onOpenChange={(next) => {
                        setInviteDialogOpen(next);
                        if (!next) setInviteError(null);
                      }}
                    >
                      <DialogTrigger asChild>
                        <Button
                          variant="default"
                          className="w-full"
                          disabled={!inviteEnabled}
                          title={
                            inviteEnabled
                              ? undefined
                              : intl.formatMessage({
                                  id: 'invoices.detail.action.invite.disabledHint',
                                  defaultMessage:
                                    'The buyer can no longer be invited for this invoice.',
                                })
                          }
                        >
                          <Send aria-hidden className="h-4 w-4" strokeWidth={1.5} />
                          {intl.formatMessage({
                            id: isResend
                              ? 'invoices.detail.action.invite.button.resend'
                              : 'invoices.detail.action.invite.button',
                            defaultMessage: isResend
                              ? 'Resend invite email'
                              : 'Email the payment link',
                          })}
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>
                            {intl.formatMessage({
                              id: isResend
                                ? 'invoices.detail.action.invite.dialog.title.resend'
                                : 'invoices.detail.action.invite.dialog.title',
                              defaultMessage: isResend
                                ? 'Resend the invite email?'
                                : 'Email the buyer?',
                            })}
                          </DialogTitle>
                          <DialogDescription>
                            {intl.formatMessage({
                              id: isResend
                                ? 'invoices.detail.action.invite.dialog.description.resend'
                                : 'invoices.detail.action.invite.dialog.description',
                              defaultMessage: isResend
                                ? 'We will email another payment link to the buyer below in case the previous email was missed.'
                                : 'We will email the payment link to the buyer below so they can pay this invoice.',
                            })}
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-2">
                          <Label htmlFor="inviteBuyerEmail">
                            {intl.formatMessage({
                              id: 'invoices.detail.action.invite.dialog.emailLabel',
                              defaultMessage: 'Buyer email',
                            })}
                          </Label>
                          <Input id="inviteBuyerEmail" value={invoice.buyerEmail ?? ''} readOnly />
                          {inviteError ? (
                            <p role="alert" className="text-xs text-[var(--color-danger)]">
                              {inviteError}
                            </p>
                          ) : null}
                        </div>
                        <DialogFooter>
                          <DialogClose asChild>
                            <Button variant="ghost">
                              {intl.formatMessage({
                                id: 'invoices.detail.action.invite.dialog.dismiss',
                                defaultMessage: 'Not yet',
                              })}
                            </Button>
                          </DialogClose>
                          <Button
                            variant="default"
                            disabled={inviteMutation.isPending}
                            onClick={() => {
                              setInviteError(null);
                              inviteMutation.mutate();
                            }}
                          >
                            {inviteMutation.isPending
                              ? intl.formatMessage({
                                  id: 'invoices.detail.action.invite.dialog.submitting',
                                  defaultMessage: 'Sending...',
                                })
                              : intl.formatMessage({
                                  id: isResend
                                    ? 'invoices.detail.action.invite.dialog.confirm.resend'
                                    : 'invoices.detail.action.invite.dialog.confirm',
                                  defaultMessage: isResend ? 'Resend email' : 'Send email',
                                })}
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </CardContent>
                </Card>
              </section>

              <section>
                <header className="mb-4 space-y-1">
                  <h2 className="text-base font-semibold text-navy-900">
                    {intl.formatMessage({
                      id: 'invoices.detail.actions.title',
                      defaultMessage: 'Actions',
                    })}
                  </h2>
                </header>
                <Card className="border-navy-100 shadow-xs">
                  <CardContent className="space-y-3 p-4">
                    <Dialog
                      open={cancelDialogOpen}
                      onOpenChange={(next) => {
                        setCancelDialogOpen(next);
                        if (!next) setCancelError(null);
                      }}
                    >
                      <DialogTrigger asChild>
                        <Button
                          variant="destructive"
                          className="w-full"
                          disabled={!cancelEnabled}
                          title={
                            cancelEnabled
                              ? undefined
                              : intl.formatMessage({
                                  id: 'invoices.detail.actions.cancel.disabledHint',
                                  defaultMessage:
                                    'Invoice cannot be cancelled in its current state.',
                                })
                          }
                        >
                          <Ban aria-hidden className="h-4 w-4" strokeWidth={1.5} />
                          {intl.formatMessage({
                            id: 'invoices.detail.actions.cancel',
                            defaultMessage: 'Cancel invoice',
                          })}
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>
                            {intl.formatMessage({
                              id: 'invoices.detail.cancel.title',
                              defaultMessage: 'Cancel invoice?',
                            })}
                          </DialogTitle>
                          <DialogDescription>
                            {intl.formatMessage({
                              id: 'invoices.detail.cancel.description',
                              defaultMessage:
                                'This stops the invoice from progressing. Let us know why.',
                            })}
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-2">
                          <Label htmlFor="cancelReason">
                            {intl.formatMessage({
                              id: 'invoices.detail.cancel.reason',
                              defaultMessage: 'Reason',
                            })}
                          </Label>
                          <Input
                            id="cancelReason"
                            value={cancelReason}
                            onChange={(e) => setCancelReason(e.target.value)}
                            maxLength={500}
                            placeholder={intl.formatMessage({
                              id: 'invoices.detail.cancel.reason.placeholder',
                              defaultMessage: 'e.g. Buyer requested termination',
                            })}
                          />
                          {cancelError ? (
                            <p role="alert" className="text-xs text-[var(--color-danger)]">
                              {cancelError}
                            </p>
                          ) : null}
                        </div>
                        <DialogFooter>
                          <DialogClose asChild>
                            <Button variant="ghost">
                              {intl.formatMessage({
                                id: 'invoices.detail.cancel.dismiss',
                                defaultMessage: 'Keep invoice',
                              })}
                            </Button>
                          </DialogClose>
                          <Button
                            variant="destructive"
                            disabled={!cancelReason.trim() || cancelMutation.isPending}
                            onClick={() => {
                              setCancelError(null);
                              cancelMutation.mutate(cancelReason.trim());
                            }}
                          >
                            {cancelMutation.isPending
                              ? intl.formatMessage({
                                  id: 'invoices.detail.cancel.submitting',
                                  defaultMessage: 'Cancelling...',
                                })
                              : intl.formatMessage({
                                  id: 'invoices.detail.cancel.confirm',
                                  defaultMessage: 'Cancel invoice',
                                })}
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                    {!cancelEnabled ? (
                      <p className="text-xs text-navy-600">
                        {intl.formatMessage({
                          id: 'invoices.detail.actions.cancel.disabledHint',
                          defaultMessage: 'Invoice cannot be cancelled in its current state.',
                        })}
                      </p>
                    ) : null}
                  </CardContent>
                </Card>
              </section>
            </aside>
          </div>
        </>
      )}
    </section>
  );
}

interface DetailFieldProps {
  label: string;
  children: ReactNode;
}

function DetailField({ label, children }: DetailFieldProps): JSX.Element {
  return (
    <div className="space-y-1">
      <p className="text-[11px] font-medium uppercase tracking-wider text-navy-700">{label}</p>
      <p className="text-sm text-navy-900">{children}</p>
    </div>
  );
}

interface TimelineEvent {
  id: string;
  invoiceId: string;
  type: string;
  source: string;
  externalEventId: string | null;
  occurredAt: string;
}

function Timeline({ events }: { events: TimelineEvent[] }): JSX.Element {
  const intl = useIntl();
  if (events.length === 0) {
    return (
      <p className="text-sm text-navy-600">
        {intl.formatMessage({
          id: 'invoices.detail.timeline.empty',
          defaultMessage: 'No events yet.',
        })}
      </p>
    );
  }
  return (
    <ol className="relative space-y-1 border-l border-navy-100 pl-6">
      {events.map((event) => (
        <TimelineRow key={event.id} event={event} />
      ))}
    </ol>
  );
}

type TimelineTone = 'success' | 'danger' | 'pending' | 'neutral';

function TimelineRow({ event }: { event: TimelineEvent }): JSX.Element {
  const intl = useIntl();
  const { Icon, tone } = pickIcon(event.type);
  const toneClass = {
    success:
      'border-[var(--color-success)]/30 bg-[var(--color-success)]/10 text-[var(--color-success)]',
    danger:
      'border-[var(--color-danger)]/30 bg-[var(--color-danger)]/10 text-[var(--color-danger)]',
    pending: 'border-navy-200 bg-white text-navy-600',
    neutral: 'border-navy-200 bg-white text-navy-700',
  }[tone];
  return (
    <li className="relative">
      <span
        className={`absolute -left-[34px] top-1.5 flex h-6 w-6 items-center justify-center rounded-full border ${toneClass}`}
      >
        <Icon aria-hidden className="h-3.5 w-3.5" strokeWidth={1.5} />
      </span>
      <div className="flex items-start gap-2 rounded-md px-2 py-2">
        <span className="flex-1">
          <span className="block font-mono text-sm font-medium text-navy-900">{event.type}</span>
          <span className="mt-0.5 block text-xs text-navy-600">
            {formatDateTime(event.occurredAt, intl.locale)}
          </span>
        </span>
      </div>
    </li>
  );
}

function pickIcon(eventType: string): { Icon: typeof Clock; tone: TimelineTone } {
  if (
    eventType === 'offramp_settled' ||
    eventType === 'buyer_kyc_approved' ||
    eventType === 'fiat_received' ||
    eventType === 'on_chain_confirmed' ||
    eventType === 'settled' ||
    eventType === 'approved'
  ) {
    return { Icon: CircleCheck, tone: 'success' };
  }
  if (
    eventType === 'rejected' ||
    eventType === 'cancelled' ||
    eventType === 'on_ramp_failed' ||
    eventType === 'offramp_failed' ||
    eventType === 'buyer_kyc_rejected'
  ) {
    return { Icon: AlertCircle, tone: 'danger' };
  }
  if (eventType === 'created') {
    return { Icon: CircleDashed, tone: 'pending' };
  }
  return { Icon: Clock, tone: 'neutral' };
}

function formatDateTime(iso: string, locale: string): string {
  try {
    return new Date(iso).toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

function formatDate(iso: string, locale: string): string {
  try {
    return new Date(iso).toLocaleDateString(locale, { dateStyle: 'medium' });
  } catch {
    return iso;
  }
}
